import React from 'react';

import { faCarSide, faEdit, faGrid2, faPlus, faTableList, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import { useListCardsInParking } from 'src/features/cards/api/useListCardsInParking';
import { useDeleteMembershipGroupCard } from 'src/features/memberships/api/useDeleteMembershipGroupCard';
import { MembershipGroupCardFormModal } from 'src/features/memberships/components/forms/MembershipGroupCardFormModal';
import { MembershipGroupCardBlock } from 'src/features/memberships/components/MembershipGroupCardBlock';
import { CellLongText } from 'src/features/memberships/components/table/cells/CellLongText';
import { type MembershipGroup, type MembershipGroupCard, VEHICLE_LAYOUT } from 'src/features/memberships/types';
import { badgeMembershipCardOptions, VEHICLE_PARKING_STATUS } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { useVehiclesLayout } from 'src/providers/MembershipVehiclesLayoutProvider';
import { useParking } from 'src/providers/ParkingProvider';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import {
    DataTable,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
    type DataTablePaginationType,
} from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ToggleGroup, ToggleGroupItem } from 'src/registry/ui/ToggleGroup';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { cn } from 'src/utils';
import { generalMessages } from 'src/utils/messages';

import type { ListResponseSmartParkAPI } from 'src/types';

type MembershipVehicleSectionProps = {
    membershipGroupCards?: ListResponseSmartParkAPI<MembershipGroupCard>;
    membershipGroup?: MembershipGroup;
    canSeeChangeCardButtons: boolean;
    isLoadingMembershipGroupData: boolean;
    pagination: DataTablePaginationType;
    handleMembershipGroupUpdate: () => void;
    refetchMembershipGroupCards: () => void;
};

const MembershipVehicleSection = ({
    membershipGroupCards,
    membershipGroup,
    canSeeChangeCardButtons,
    isLoadingMembershipGroupData,
    pagination,
    handleMembershipGroupUpdate,
    refetchMembershipGroupCards,
}: MembershipVehicleSectionProps) => {
    const { formatMessage } = useIntl();
    const { parkingId, parkingRole } = useParking();
    const { getVehiclesLayout, setVehiclesLayout } = useVehiclesLayout();
    const vehicleLayout = getVehiclesLayout(membershipGroup?.id);
    const [selectedMembershipGroupCard, setSelectedMembershipGroupCard] = React.useState<MembershipGroupCard | null>(
        null,
    );
    const [selectedDeleteMembershipGroupCard, setSelectedDeleteMembershipGroupCard] =
        React.useState<MembershipGroupCard | null>(null);

    const [isCardModalOpen, setIsCardModalOpen] = React.useState(false);
    const [modalMode, setModalMode] = React.useState<'edit' | 'add' | undefined>(undefined);

    const isClient = parkingRole === MEMBERSHIP_ROLES.CLIENT;

    const { data: inParkingCards, isLoading: isLoadingInParkingCards } = useListCardsInParking({
        parkingId,
        card_number: membershipGroupCards?.results.map((card) => card.number).join(','),
        config: { enabled: !!membershipGroupCards },
    });

    const { mutate: deleteMembershipGroupCard } = useDeleteMembershipGroupCard({
        onSuccess: () => {
            void handleMembershipGroupUpdate();
            setSelectedDeleteMembershipGroupCard(null);
            toast({
                title: formatMessage(membershipMessages.deletedCard),
            });
        },
    });

    const isInParkingBadge = (vehicleNumber: string) => {
        if (!inParkingCards) return badgeMembershipCardOptions[VEHICLE_PARKING_STATUS.OUT_OF_PARKING];
        const foundCard = inParkingCards.results?.find((inParkingCard) => inParkingCard.card_number === vehicleNumber);
        if (foundCard) {
            return badgeMembershipCardOptions[VEHICLE_PARKING_STATUS.IN_PARKING];
        } else {
            return badgeMembershipCardOptions[VEHICLE_PARKING_STATUS.OUT_OF_PARKING];
        }
    };

    const openCardModal = (card: MembershipGroupCard) => {
        setSelectedMembershipGroupCard(card);
        setIsCardModalOpen(true);
        setModalMode('edit');
    };

    const closeCardModal = (isOpen: boolean) => {
        setSelectedMembershipGroupCard(null);
        setIsCardModalOpen(isOpen);
    };

    const columnHelper = createColumnHelper<MembershipGroupCard>();

    const membershipVehiclesActions = canSeeChangeCardButtons
        ? [
              columnHelper.display({
                  header: DataTableColumnGenericHeaderDefinition,
                  id: 'actions',
                  cell: (props) => (
                      <div className="ml-auto flex max-w-24 gap-1">
                          <TooltipProvider>
                              <Tooltip>
                                  <TooltipTrigger asChild>
                                      <Button
                                          onClick={() => {
                                              openCardModal(props.row.original);
                                              setModalMode('edit');
                                          }}
                                          size="icon"
                                          variant="link"
                                      >
                                          <FontAwesomeIcon icon={faEdit} />
                                      </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>{formatMessage(membershipMessages.editVehicle)}</TooltipContent>
                              </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                              <Tooltip>
                                  <TooltipTrigger asChild>
                                      <Button
                                          onClick={() => setSelectedDeleteMembershipGroupCard(props.row.original)}
                                          size="icon"
                                          variant="link"
                                      >
                                          <FontAwesomeIcon icon={faTrashAlt} />
                                      </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>{formatMessage(membershipMessages.deleteVehicle)}</TooltipContent>
                              </Tooltip>
                          </TooltipProvider>
                      </div>
                  ),
              }),
          ]
        : [];

    const columns = [
        columnHelper.accessor('number', {
            header: (props) => (
                <DataTableColumnHeader title={formatMessage(membershipMessages.vehicleLicensePlate)} {...props} />
            ),
        }),
        columnHelper.accessor('number', {
            header: DataTableColumnHeader,
            cell: (props) => {
                const vehicleNumber = props.row.original.number;
                if (!vehicleNumber) return '-';
                return (
                    <Badge theme={isInParkingBadge(vehicleNumber).theme} className="gap-1.5 whitespace-nowrap">
                        {formatMessage(isInParkingBadge(vehicleNumber).label)}
                    </Badge>
                );
            },
            meta: {
                title: formatMessage(membershipMessages.inParking),
                badgeOptions: badgeMembershipCardOptions,
            },
            id: 'in_parking',
        }),
        columnHelper.accessor('tags.card_tag.text', {
            header: DataTableColumnHeader,
            cell: (props) => {
                const tags = props.row.original.tags;
                return !isClient && tags?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => {
                            return (
                                <Badge key={`card-tag-${tag.id}`} theme={tag.card_tag.theme}>
                                    {tag.card_tag.text}
                                </Badge>
                            );
                        })}
                    </div>
                ) : null;
            },
            meta: {
                title: formatMessage(membershipMessages.tagsColumn),
                badgeOptions: badgeMembershipCardOptions,
            },
            id: 'tags',
        }),
        columnHelper.accessor('description', {
            header: DataTableColumnHeader,
            cell: CellLongText,
            meta: {
                title: formatMessage(membershipMessages.description),
                isLongText: true,
            },
        }),
        ...membershipVehiclesActions,
    ];

    return (
        <>
            {canSeeChangeCardButtons && membershipGroup && (
                <MembershipGroupCardFormModal
                    onSuccess={handleMembershipGroupUpdate}
                    parkingId={parkingId}
                    membershipGroup={membershipGroup}
                    membershipGroupCard={selectedMembershipGroupCard}
                    isCardModalOpen={isCardModalOpen}
                    setIsCardModalOpen={closeCardModal}
                    modalMode={modalMode}
                />
            )}

            {canSeeChangeCardButtons && (
                <Dialog
                    open={!!selectedDeleteMembershipGroupCard}
                    onOpenChange={() => setSelectedDeleteMembershipGroupCard(null)}
                >
                    <DialogContent>
                        <DialogHeader className="flex-row gap-4">
                            <SquareIcon icon={faTrashAlt} />
                            <div>
                                <DialogTitle>{formatMessage(membershipMessages.deleteVehicle)}</DialogTitle>
                                <DialogDescription>
                                    {formatMessage(membershipMessages.deleteVehicleDescription, {
                                        vehicleNumber: selectedDeleteMembershipGroupCard?.number,
                                    })}
                                </DialogDescription>
                            </div>
                        </DialogHeader>

                        <DialogSeparator />

                        <DialogBody>
                            <p className="text-gray-500">
                                {formatMessage(membershipMessages.confirmDeleteVehicle, {
                                    name: selectedDeleteMembershipGroupCard?.number,
                                })}
                            </p>
                        </DialogBody>

                        <DialogSeparator />

                        <DialogFooter>
                            <DialogClose asChild>
                                <Button variant="outline" type="button" className="flex-1">
                                    {formatMessage(generalMessages.cancel)}
                                </Button>
                            </DialogClose>

                            <Button
                                variant="destructiveLight"
                                onClick={() => {
                                    if (selectedDeleteMembershipGroupCard && membershipGroup) {
                                        void deleteMembershipGroupCard({
                                            membershipGroupCardId: selectedDeleteMembershipGroupCard.id,
                                            membershipGroupId: membershipGroup?.id,
                                            parkingId,
                                        });
                                    }
                                }}
                                className="flex-1"
                            >
                                {formatMessage(membershipMessages.deleteVehicle)}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            <SectionBlock>
                <SectionBlockHeader>
                    <SectionBlockHead className="flex flex-row justify-between">
                        <SectionBlockTitle>
                            <div className="flex flex-row">
                                <FormattedMessage {...membershipMessages.vehicles} />
                                {vehicleLayout === VEHICLE_LAYOUT.GRID && (
                                    <Badge theme="primary" className="ml-2 shrink-0">
                                        {membershipGroupCards?.count}{' '}
                                        {membershipGroupCards?.count === 1
                                            ? formatMessage(generalMessages.vehicle)
                                            : formatMessage(generalMessages.vehicles)}
                                    </Badge>
                                )}
                            </div>
                        </SectionBlockTitle>
                        <ToggleGroup
                            type="single"
                            value={vehicleLayout}
                            onValueChange={(val) => {
                                if (val && (val as VEHICLE_LAYOUT) !== vehicleLayout) {
                                    setVehiclesLayout(membershipGroup?.id, val as VEHICLE_LAYOUT);
                                }
                            }}
                        >
                            <ToggleGroupItem value={VEHICLE_LAYOUT.GRID}>
                                <FontAwesomeIcon icon={faGrid2} className="text-primary-700" />
                            </ToggleGroupItem>
                            <ToggleGroupItem value={VEHICLE_LAYOUT.TABLE}>
                                <FontAwesomeIcon icon={faTableList} className="text-primary-700" />
                            </ToggleGroupItem>
                        </ToggleGroup>
                    </SectionBlockHead>

                    {canSeeChangeCardButtons && membershipGroup && (
                        <Button
                            onClick={() => {
                                setIsCardModalOpen(true);
                                setModalMode('add');
                            }}
                            variant="outline"
                            size="sm"
                        >
                            <FontAwesomeIcon icon={faPlus} className="text-primary-700" />
                            <FormattedMessage {...membershipMessages.addVehicle} />
                        </Button>
                    )}
                </SectionBlockHeader>
                <SectionBlockSeparator />
                <SectionBlockBody
                    className={cn('grid', {
                        'xs:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4': vehicleLayout === VEHICLE_LAYOUT.GRID,
                    })}
                >
                    {isLoadingMembershipGroupData && (
                        <>
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                        </>
                    )}

                    {vehicleLayout === VEHICLE_LAYOUT.GRID && (
                        <>
                            {membershipGroupCards?.count == 0 && (
                                <GeneralMessageBlock
                                    title={formatMessage(membershipMessages.noVehicles)}
                                    subtitle={formatMessage(membershipMessages.noVehiclesDescription)}
                                    icon={faCarSide}
                                    className="xs:col-span-2 py-20 xl:col-span-3 2xl:col-span-4"
                                />
                            )}
                            {membershipGroupCards?.results.map((card) => (
                                <MembershipGroupCardBlock
                                    key={card.id}
                                    card={card}
                                    inParkingCards={inParkingCards?.results}
                                    isLoadingInParkingCards={isLoadingInParkingCards}
                                    canSeeChangeCardButtons={canSeeChangeCardButtons}
                                    setSelectedDeleteMembershipGroupCard={setSelectedDeleteMembershipGroupCard}
                                    setSelectedMembershipGroupCard={openCardModal}
                                    isClient={isClient}
                                />
                            ))}
                        </>
                    )}

                    {vehicleLayout === VEHICLE_LAYOUT.TABLE && (
                        <DataTable
                            columns={columns}
                            data={membershipGroupCards}
                            refetch={refetchMembershipGroupCards}
                            pagination={pagination}
                            isLoading={isLoadingMembershipGroupData}
                            semantics={{
                                title: formatMessage(generalMessages.vehicles),
                                entryName: formatMessage(generalMessages.vehicle),
                                entryNames: formatMessage(generalMessages.vehicles),
                            }}
                            components={{
                                controls: DataTableControls,
                                pagination: DataTablePagination,
                            }}
                            enableLocalFilters={true}
                        />
                    )}
                </SectionBlockBody>
            </SectionBlock>
        </>
    );
};

export default MembershipVehicleSection;
